import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Grid2,
  InputAdornment,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { observer, useLocalObservable } from "mobx-react";
import { runInAction } from "mobx";
import React, { useEffect, useRef } from "react";
import { PurchaseRequestSkuVariant } from "../../../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import { OrderProductVariantsModuleStore } from "../../../../stores/OrderProductVariantsModuleStore";
import {
  fromCatalogProductVariant,
  fromOrderProductsVariant,
  isPurchaseRequestSkuVariantSelected,
  mergePurchaseRequestSkuVariants,
  PurchaseRequestSkuProductContext,
} from "../../../../../../utils/purchaseRequestSkuVariants";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import { useDebounce } from "../../../../../../views/components/common/hooks/Debounce";
import ProductSkuBuilder from "../../products/ProductSkuBuilder";
import {
  UpdateVariantsInput,
  getApiErrorMessage,
} from "../../../../../../models/CatalogProducts/types";

export interface PurchaseRequestSkuValidation {
  product?: string;
  rows?: Record<
    string,
    {
      quantity?: string;
      costPrice?: string;
      stock?: string;
    }
  >;
}

interface PurchaseRequestSkuVariantsProps {
  orderId: number;
  productId: number;
  productName: string;
  categoryId?: number | null;
  productSku?: string | null;
  defaultCostPrice?: number;
  productDescription?: string;
  productGstPercentage?: number;
  productHsnCode?: string | null;
  productSellingPrice?: number;
  expanded: boolean;
  value: PurchaseRequestSkuVariant[];
  validation?: PurchaseRequestSkuValidation;
  requireCostPrice?: boolean;
  requestLabel?: string;
  onExpandedChange: (expanded: boolean) => void;
  onChange: (variants: PurchaseRequestSkuVariant[]) => void;
}

type InventoryTab = "list" | "create";

const PurchaseRequestSkuVariants: React.FC<PurchaseRequestSkuVariantsProps> = ({
  orderId,
  productId,
  productName,
  categoryId,
  productSku,
  defaultCostPrice = 0,
  productDescription,
  productGstPercentage,
  productHsnCode,
  productSellingPrice,
  expanded,
  value,
  validation,
  requireCostPrice = true,
  requestLabel = "PR",
  onExpandedChange,
  onChange,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const moduleStore = useRef(new OrderProductVariantsModuleStore()).current;
  const valueRef = useRef(value);
  valueRef.current = value;

  const productContext: PurchaseRequestSkuProductContext = {
    product_name: productName,
    description: productDescription,
    gst_percentage: productGstPercentage,
    hsn_code: productHsnCode,
    defaultCostPrice,
    defaultSellingPrice: productSellingPrice,
  };

  const page = useLocalObservable(() => ({
    loading: false,
    creating: false,
    error: null as string | null,
    search: "",
    inventoryTab: "list" as InventoryTab,
    resolvedCategoryId: categoryId ?? null,
    resolvedProductSku: productSku ?? null,
    pendingVariants: [] as UpdateVariantsInput["variants"],
    builderResetKey: 0,
    setSearch(search: string) {
      this.search = search;
    },
    setInventoryTab(tab: InventoryTab) {
      this.inventoryTab = tab;
      this.error = null;
      if (tab === "list") this.pendingVariants = [];
    },
  }));

  const isCompanyInward = orderId === -1;
  const debouncedSearch = useDebounce(page.search, 400);

  const loadVariants = async () => {
    try {
      runInAction(() => {
        page.loading = true;
        page.error = null;
      });

      if (isCompanyInward) {
        const detail = await moduleStore.loadProductDetail(productId);
        const catalogVariants =
          await moduleStore.loadCatalogVariants(productId);
        if (!detail) throw new Error("Product details were not found.");

        const source = catalogVariants
          .map((variant) => fromCatalogProductVariant(variant, productContext))
          .filter((variant) => Boolean(variant.sku));
        runInAction(() => {
          page.resolvedCategoryId = detail.category_id || categoryId || null;
          page.resolvedProductSku = detail.sku || productSku || null;
        });
        onChange(
          mergePurchaseRequestSkuVariants(
            source,
            valueRef.current,
            productContext,
          ),
        );
      } else {
        const orderVariants = await moduleStore.loadOrderVariants(
          orderId,
          productId,
          true,
        );
        const source = orderVariants
          .map((variant) => fromOrderProductsVariant(variant, productContext))
          .filter((variant) => Boolean(variant.sku));
        onChange(
          mergePurchaseRequestSkuVariants(
            source,
            valueRef.current,
            productContext,
          ),
        );
      }
    } catch (error) {
      runInAction(() => {
        page.error = getApiErrorMessage(error, "Could not load SKU variants.");
      });
    } finally {
      runInAction(() => {
        page.loading = false;
      });
    }
  };

  useEffect(() => {
    void loadVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, productId]);

  const visibleVariants = value.filter((variant) => {
    const search = debouncedSearch.trim().toLowerCase();
    if (!search) return true;
    return (
      variant.sku.toLowerCase().includes(search) ||
      variant.title.toLowerCase().includes(search)
    );
  });

  const selectedCount = value.filter(
    isPurchaseRequestSkuVariantSelected,
  ).length;
  const completedCount = value.filter(
    (variant) =>
      isPurchaseRequestSkuVariantSelected(variant) &&
      Number(variant.quantity) > 0 &&
      (!requireCostPrice || Number(variant.cost_price) > 0),
  ).length;

  const allVisibleSelected =
    visibleVariants.length > 0 &&
    visibleVariants.every(isPurchaseRequestSkuVariantSelected);
  const someVisibleSelected = visibleVariants.some(
    isPurchaseRequestSkuVariantSelected,
  );

  const updateLine = (
    sku: string,
    patch: Partial<PurchaseRequestSkuVariant>,
  ) => {
    const nextPatch = { ...patch };
    if (patch.cost_price !== undefined) {
      nextPatch.rate = patch.cost_price;
    }
    onChange(
      value.map((line) =>
        line.sku === sku ? { ...line, ...nextPatch } : line,
      ),
    );
  };

  const setVariantSelected = (sku: string, selected: boolean) => {
    onChange(
      valueRef.current.map((line) =>
        line.sku === sku ? { ...line, selected } : { ...line },
      ),
    );
  };

  const setAllVisibleSelected = (selected: boolean) => {
    const visibleSkus = new Set(visibleVariants.map((variant) => variant.sku));
    onChange(
      valueRef.current.map((line) =>
        visibleSkus.has(line.sku) ? { ...line, selected } : { ...line },
      ),
    );
  };

  const handleCreateCatalogVariants = async () => {
    if (page.pendingVariants.length === 0) return;
    try {
      runInAction(() => {
        page.creating = true;
        page.error = null;
      });
      const created = await moduleStore.createCatalogVariants(
        productId,
        page.pendingVariants,
      );
      const createdLines = created
        .map((variant) => fromCatalogProductVariant(variant, productContext))
        .filter((variant) => Boolean(variant.sku));
      const currentSkus = new Set(
        valueRef.current.map((line) => line.sku.trim().toLowerCase()),
      );
      const next = [
        ...valueRef.current,
        ...createdLines.filter(
          (line) => !currentSkus.has(line.sku.trim().toLowerCase()),
        ),
      ];
      onChange(next);
      runInAction(() => {
        page.inventoryTab = "list";
        page.pendingVariants = [];
        page.builderResetKey += 1;
      });
    } catch (error) {
      runInAction(() => {
        page.error = getApiErrorMessage(
          error,
          "Could not create catalog variant.",
        );
      });
    } finally {
      runInAction(() => {
        page.creating = false;
      });
    }
  };

  return (
    <Box mb={2}>
      <Accordion
        expanded={expanded}
        onChange={(_, next) => onExpandedChange(next)}
        disableGutters
        elevation={0}
        sx={{
          mt: 2,
          border: `${validation?.product ? 2 : 1}px solid ${
            validation?.product ? colors.errorRed : colors.greyBorder
          }`,
          borderRadius: "8px !important",
          overflow: "hidden",
          "&::before": { display: "none" },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 2,
            minHeight: 52,
            backgroundColor: colors.greyBackground,
            "& .MuiAccordionSummary-content": { my: 1 },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            width="100%"
            pr={1}
          >
            <Box minWidth={0}>
              <CustomTypography variant="body2" fontWeight={700}>
                SKU variants
              </CustomTypography>
              <CustomTypography variant="caption" color={colors.greyTextColor}>
                {value.length} available · {completedCount} added to {requestLabel}
              </CustomTypography>
            </Box>
            {selectedCount > 0 ? (
              <Chip
                size="small"
                color="primary"
                label={`${selectedCount} selected`}
                sx={{ height: 22, fontWeight: 600 }}
              />
            ) : null}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 2 }}>
          {validation?.product ? (
            <CustomTypography
              variant="body3"
              color={colors.errorRed}
              sx={{ display: "block", mb: 1.5 }}
            >
              {validation.product}
            </CustomTypography>
          ) : null}

          {page.error ? (
            <CustomTypography
              variant="body3"
              color={colors.errorRed}
              sx={{ display: "block", mb: 1.5 }}
            >
              {page.error}
            </CustomTypography>
          ) : null}

          {isCompanyInward ? (
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
              <Chip
                clickable
                label="Available SKUs"
                color={page.inventoryTab === "list" ? "primary" : "default"}
                variant={page.inventoryTab === "list" ? "filled" : "outlined"}
                onClick={() => page.setInventoryTab("list")}
                sx={{ fontWeight: 600 }}
              />
              <Chip
                clickable
                icon={<AddIcon />}
                label="Create new SKU"
                color={page.inventoryTab === "create" ? "primary" : "default"}
                variant={page.inventoryTab === "create" ? "filled" : "outlined"}
                onClick={() => page.setInventoryTab("create")}
                disabled={!page.resolvedCategoryId || page.creating}
                sx={{ fontWeight: 600 }}
              />
            </Box>
          ) : null}

          {page.inventoryTab === "list" ? (
            <>
              <TextField
                variant="standard"
                fullWidth
                placeholder="Search by SKU or title"
                value={page.search}
                disabled={page.loading}
                onChange={(event) => page.setSearch(event.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ color: colors.greyTextColor, fontSize: "1rem" }}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  mb: 2,
                  maxWidth: 420,
                  "& .MuiInputBase-input": { fontSize: "0.8rem" },
                }}
              />

              {page.loading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : visibleVariants.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, borderColor: colors.greyBorder }}
                >
                  <CustomTypography
                    variant="body3"
                    color={colors.greyTextColor}
                  >
                    {page.search.trim()
                      ? "No SKU matches your search."
                      : isCompanyInward
                        ? "No catalog SKUs found. Use Create new SKU."
                        : `No order SKUs found for ${productName}. Add variants to the order first.`}
                  </CustomTypography>
                </Paper>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.25}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    mb={0.5}
                    sx={{ width: "fit-content" }}
                  >
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      disabled={visibleVariants.length === 0}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (visibleVariants.length === 0) return;
                        setAllVisibleSelected(!allVisibleSelected);
                      }}
                      onChange={() => undefined}
                      inputProps={{
                        "aria-label": "Select all variants",
                      }}
                    />
                    <CustomTypography
                      variant="body3"
                      fontWeight={600}
                      sx={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => {
                        if (visibleVariants.length === 0) return;
                        setAllVisibleSelected(!allVisibleSelected);
                      }}
                    >
                      Select all variants
                    </CustomTypography>
                  </Box>
                  {visibleVariants.map((variant) => {
                    const rowError = (() => {
                      const rows = validation?.rows;
                      if (!rows) return undefined;
                      if (rows[variant.sku]) return rows[variant.sku];
                      const skuKey = variant.sku.trim().toLowerCase();
                      const match = Object.keys(rows).find(
                        (key) => key.trim().toLowerCase() === skuKey,
                      );
                      return match ? rows[match] : undefined;
                    })();
                    const hasRowError = Boolean(
                      rowError?.quantity ||
                        rowError?.costPrice ||
                        rowError?.stock,
                    );
                    const isSelected =
                      isPurchaseRequestSkuVariantSelected(variant);
                    return (
                      <Paper
                        key={variant.sku}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          borderWidth: hasRowError ? 2 : 1,
                          borderColor: hasRowError
                            ? colors.errorRed
                            : colors.greyBorder,
                          backgroundColor: isSelected
                            ? undefined
                            : colors.greyBackground,
                        }}
                      >
                        <Grid2
                          container
                          spacing={1.5}
                          columns={12}
                          alignItems="flex-start"
                        >
                          <Grid2
                            size={{ xs: 12, md: 5 }}
                            sx={{ display: "flex", gap: 1, minWidth: 0 }}
                          >
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setVariantSelected(variant.sku, !isSelected);
                              }}
                              onChange={() => undefined}
                              sx={{ mt: 0.25, p: 0.5, flexShrink: 0 }}
                              inputProps={{
                                "aria-label": `Select ${variant.sku} for ${requestLabel}`,
                              }}
                            />
                            <Box
                              minWidth={0}
                              sx={{
                                cursor: "pointer",
                                opacity: isSelected ? 1 : 0.7,
                              }}
                              onClick={() => {
                                setVariantSelected(variant.sku, !isSelected);
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                fontWeight={700}
                              >
                                {variant.title || "Untitled variant"}
                              </CustomTypography>
                              <SkuCopyRow sku={variant.sku} />
                            </Box>
                          </Grid2>
                          <Grid2
                            size={{ xs: 6, md: 3.5 }}
                            sx={{ opacity: isSelected ? 1 : 0.55 }}
                          >
                            <CustomTypography
                              variant="caption"
                              color={colors.greyTextColor}
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              Quantity
                            </CustomTypography>
                            <CustomTextField
                              width="100%"
                              type="number"
                              allowNegativeValue={false}
                              allowDecimalValue={false}
                              placeholder="0"
                              disabled={!isSelected}
                              value={
                                variant.quantity === null ||
                                variant.quantity === undefined
                                  ? ""
                                  : variant.quantity
                              }
                              onChange={(event) => {
                                if (!isSelected) return;
                                const raw = event.target.value;
                                updateLine(variant.sku, {
                                  quantity:
                                    raw === "" ? undefined : Number(raw),
                                });
                              }}
                              error={Boolean(rowError?.quantity)}
                              helperText={rowError?.quantity}
                            />
                          </Grid2>
                          {requireCostPrice ? (
                          <Grid2
                            size={{ xs: 6, md: 3.5 }}
                            sx={{ opacity: isSelected ? 1 : 0.55 }}
                          >
                            <CustomTypography
                              variant="caption"
                              color={colors.greyTextColor}
                              sx={{ display: "block", mb: 0.5 }}
                            >
                              Cost price
                            </CustomTypography>
                            <CustomTextField
                              width="100%"
                              type="number"
                              allowNegativeValue={false}
                              allowDecimalValue
                              placeholder="0"
                              disabled={!isSelected}
                              value={variant.cost_price ?? ""}
                              onChange={(event) => {
                                if (!isSelected) return;
                                const raw = event.target.value;
                                updateLine(variant.sku, {
                                  cost_price:
                                    raw === "" ? undefined : Number(raw),
                                });
                              }}
                              error={Boolean(rowError?.costPrice)}
                              helperText={rowError?.costPrice}
                            />
                          </Grid2>
                          ) : null}
                        </Grid2>
                        {rowError?.stock ? (
                          <CustomTypography
                            variant="caption"
                            color={colors.errorRed}
                            sx={{ display: "block", mt: 1, fontWeight: 600 }}
                          >
                            {rowError.stock}
                          </CustomTypography>
                        ) : null}
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </>
          ) : (
            <Box>
              <CustomTypography
                variant="body3"
                color={colors.greyTextColor}
                sx={{ display: "block", mb: 2 }}
              >
                Build a missing catalog SKU. It will be added to this list
                automatically.
              </CustomTypography>
              {page.resolvedCategoryId ? (
                <>
                  <ProductSkuBuilder
                    key={page.builderResetKey}
                    categoryId={page.resolvedCategoryId}
                    serverSku={page.resolvedProductSku}
                    resetKey={page.builderResetKey}
                    disabled={page.creating}
                    onVariantsChange={(variants) => {
                      runInAction(() => {
                        page.pendingVariants = variants;
                      });
                    }}
                  />
                  {page.pendingVariants.length > 0 ? (
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <AppButton
                        variant="primary"
                        label={
                          page.creating
                            ? "Creating…"
                            : `Create ${page.pendingVariants.length} SKU${
                                page.pendingVariants.length === 1 ? "" : "s"
                              }`
                        }
                        disabled={page.creating}
                        onClick={() => {
                          void handleCreateCatalogVariants();
                        }}
                      />
                    </Box>
                  ) : null}
                </>
              ) : (
                <CustomTypography variant="body3" color={colors.errorRed}>
                  This product needs a primary category before a SKU can be
                  created.
                </CustomTypography>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default observer(PurchaseRequestSkuVariants);
